import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap, filter } from 'rxjs/operators';
import { BrandIndexService } from './brand-index.service';

export interface EducationConfigResponse {
    courses?: string[];
    countries?: string[];
    default_models?: string[];
}

@Injectable({
    providedIn: 'root'
})
export class EducationConfigService {
    private configSubject = new BehaviorSubject<EducationConfigResponse | null>(null);
    private loadingSubject = new BehaviorSubject<boolean>(false);
    private errorSubject = new BehaviorSubject<string | null>(null);

    public config$ = this.configSubject.asObservable();
    public loading$ = this.loadingSubject.asObservable();
    public error$ = this.errorSubject.asObservable();

    constructor(private brandIndexService: BrandIndexService) { }

    /**
     * Get education config from brand-index API (cached).
     */
    getEducationConfig(): Observable<EducationConfigResponse> {
        if (this.configSubject.value) {
            return of(this.configSubject.value);
        }

        if (this.loadingSubject.value) {
            return this.config$.pipe(
                filter((config): config is EducationConfigResponse => config !== null)
            );
        }

        this.loadingSubject.next(true);
        this.errorSubject.next(null);

        return this.brandIndexService.getEducationConfig().pipe(
            tap((config: EducationConfigResponse) => {
                this.configSubject.next(config);
                this.loadingSubject.next(false);
            }),
            catchError((error) => {
                this.errorSubject.next(error?.message || 'Failed to load config');
                this.loadingSubject.next(false);
                throw error;
            })
        );
    }

    refreshEducationConfig(): Observable<EducationConfigResponse> {
        this.configSubject.next(null);
        this.loadingSubject.next(true);
        this.errorSubject.next(null);

        return this.brandIndexService.getEducationConfig().pipe(
            tap((config: EducationConfigResponse) => {
                this.configSubject.next(config);
                this.loadingSubject.next(false);
            }),
            catchError((error) => {
                this.errorSubject.next(error?.message || 'Failed to load education config');
                this.loadingSubject.next(false);
                throw error;
            })
        );
    }

    getCurrentConfig(): EducationConfigResponse | null {
        return this.configSubject.value;
    }

    clearCache(): void {
        this.configSubject.next(null);
        this.errorSubject.next(null);
    }
}
